import React, { useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  Button,
  Box,
  MenuItem,
  IconButton,
  Stack,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import {
  formatPhoneNumber,
  isValidName,
  isValidEmail,
  isValidPhone,
} from "../../utils/validation";
import { useSnackbarAlert } from "../../hooks/useSnackbarAlert";

const BookingForm = ({ selectedSession, form, setForm, pricePerPerson }) => {
  const [submitting, setSubmitting] = useState(false);
  const [etransferConfirmed, setEtransferConfirmed] = useState(false);
  const snackbar = useSnackbarAlert();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const spotsLeft = selectedSession.capacity - selectedSession.bookings.length;
  const displayDate = new Date(selectedSession.date).toLocaleDateString(
    "en-US",
    {
      month: "long",
      day: "numeric",
      year: "numeric",
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processed = value;
    if (name === "phone") processed = formatPhoneNumber(value);
    setForm({ ...form, [name]: processed });
  };

  const calculateBreakdown = (numSeats, price) => {
    const subtotal = numSeats * price;
    const tax = +(subtotal * 0.15).toFixed(2);
    const total = +(subtotal + tax).toFixed(2);
    return { subtotal, tax, total };
  };

  const validate = () => {
    if (!isValidName(form.firstName)) return "First name is invalid.";
    if (!isValidName(form.lastName)) return "Last name is invalid.";
    if (!isValidEmail(form.email)) return "Email is invalid.";
    if (!isValidPhone(form.phone)) return "Phone number must be 10 digits.";
    if (!form.numSeats || form.numSeats < 1 || form.numSeats > spotsLeft)
      return "Please select a valid number of seats.";
    if (!etransferConfirmed)
      return "Please confirm the e-transfer before submitting.";
    return null;
  };

  const handleSubmit = async () => {
    const error = validate();
    if (error) {
      snackbar.error(error);
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post("/classes/bookings", {
        sessionId: selectedSession._id,
        ...form,
      });
      snackbar.success("Booking successful!");
      navigate(`/classes/decorating/confirmation/${res.data.booking._id}`);
    } catch (err) {
      const msg =
        err.response?.data?.error || "Something went wrong. Please try again.";
      snackbar.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const { subtotal, tax, total } = calculateBreakdown(
    form.numSeats,
    pricePerPerson
  );

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        {isMobile ? (
          <>
            <Button
              onClick={() => window.location.reload()}
              startIcon={<ArrowBackIcon />}
              fullWidth
              variant="outlined"
              sx={{ mb: 3 }}>
              Back to Sessions
            </Button>
            <Typography variant="h5" fontWeight="bold">
              Booking for {displayDate} @ {selectedSession.startTime}
            </Typography>
          </>
        ) : (
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={() => window.location.reload()}
              sx={{ p: 0, mr: 1 }}
              aria-label="Back to Sessions">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" fontWeight="bold">
              Booking for {displayDate} @ {selectedSession.startTime}
            </Typography>
          </Stack>
        )}

        <Typography variant="subtitle1" sx={{ mb: 2, mt: isMobile ? 1 : 0 }}>
          Price per person: ${pricePerPerson.toFixed(2)} + HST
        </Typography>

        <Box
          sx={{
            mb: 3,
            p: 2,
            backgroundColor: "#fff9c4",
            borderRadius: 2,
          }}>
          <Typography variant="body2" color="text.secondary">
            ⚠️ This class requires at least {selectedSession.minGroupSize || 5}{" "}
            participants to run. If a group does not form, we’ll notify you and
            refund your payment.
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            label="First Name"
            name="firstName"
            value={form.firstName}
            onChange={handleChange}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Last Name"
            name="lastName"
            value={form.lastName}
            onChange={handleChange}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Email"
            name="email"
            value={form.email}
            onChange={handleChange}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Phone"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            select
            label="Number of People"
            name="numSeats"
            value={form.numSeats}
            onChange={handleChange}
            fullWidth
            required>
            {Array.from({ length: spotsLeft }, (_, i) => i + 1).map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Notes (optional)"
            name="notes"
            value={form.notes}
            onChange={handleChange}
            fullWidth
            multiline
            rows={isSmall ? 3 : 2}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Order Summary
        </Typography>
        <Typography>Subtotal: ${subtotal.toFixed(2)}</Typography>
        <Typography>Tax (15%): ${tax.toFixed(2)}</Typography>
        <Typography fontWeight="bold">Total: ${total.toFixed(2)}</Typography>
      </Box>

      <Box sx={{ mt: 3 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="checkbox"
            checked={etransferConfirmed}
            onChange={(e) => setEtransferConfirmed(e.target.checked)}
          />
          <Typography variant="body2">
            I confirm an e-transfer of ${total.toFixed(2)} has been sent to{" "}
            <strong>payment@cakeyhand.com</strong>
          </Typography>
        </label>
      </Box>

      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        disabled={submitting || !etransferConfirmed}
        sx={{ mt: 3, minHeight: 42 }}>
        {submitting ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          "Confirm Booking"
        )}
      </Button>
    </Box>
  );
};

export default BookingForm;
